<template>
  <div class="p-test-table-container">
    <!-- ヘッダー部分 -->
    <div class="p-test-table-header">
      <div class="p-test-table-info-group">
        <div>{{ academicSession?.name }}</div>
        <div>{{ academicYear?.name }}</div>
        <div>
          <div class="c-form-select -type2 -small jsSelectBox -active-type3">
            <div class="output" data-unique_id="class">
              <input type="hidden" name="class" :value="selectedClassroom?.uuid">
              <div class="label u-text-ellipsis">{{ selectedClassroom?.name }}</div>
              <div class="c-form-select__close" id="jsClass"></div>
            </div>
            <div class="option">
              <ul class="option__list">
                <li class="optionClass"
                  v-for="classroom in classrooms"
                  :key="classroom.uuid"
                  :data-select-value="classroom.uuid"
                  @click="changeClassroom(classroom)"
                >
                  <span class="label">{{ classroom.name }}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div>{{ subjectField?.name }}</div>
        <div>
          <div class="c-form-select -type2 -middle2 jsSelectBox -active-type3">
            <div class="output" data-unique_id="title">
              <input type="hidden" name="title" :value="selectedCbtUnit?.id">
              <div class="label u-text-ellipsis"> {{ removeMarkDown(selectedCbtUnit?.title) }}
              </div>
              <div class="c-form-select__close" id="jsTitle"></div>
            </div>
            <div class="option">
              <ul class="option__list">
                <li class="optionTitle"
                  v-for="cbt_unit in cbtUnits"
                  :key="cbt_unit.id"
                  :data-select-value="cbt_unit.id"
                  @click="changeCbtUnit(cbt_unit)"
                >
                  <span class="label">
                    {{ removeMarkDown(cbt_unit.title) }}
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <div class="p-test-table-status">
        <div>提出状況</div>
        <div><strong class="u-text-alert">{{ cbtClassroomStudentStatus.number_of_submitted_students }}</strong> <span class="normal-font">/ {{ cbtClassroomStudentStatus.total_number_of_students }}（人）</span></div>
      </div>
    </div>
    <!-- ローディング中 -->
    <div v-if="isLoading" class="c-table-test-list-wrapper -answer -loading"></div>
    <!-- データがない場合の表示 -->
    <div v-else-if="showBlankPage" class="c-table-test-list-wrapper -answer -empty">
      <div class="c-empty-message">
        <div class="c-empty-message__icon"><img src="../../../../djangoapp/assets/teacher/img/common/icon_empty.svg" alt=""></div>
        <div class="c-empty-message__title">データがありません</div>
        <div class="c-empty-message__text">
          選択した条件で、テストが実施されていません。<br>
          実施状況をご確認ください。
        </div>
      </div>
    </div>
    <template v-else>
      <div class="p-test-table-legend">
        <div>正答率</div>
        <div class="p-test-table-legend-item">
          <span class="p-test-table-color-box p-test-table-color-red-medium"></span>
          <span>0-20%</span>
        </div>
        <div class="p-test-table-legend-item">
          <span class="p-test-table-color-box p-test-table-color-red-light"></span>
          <span>21-40%</span>
        </div>
        <div class="p-test-table-legend-item">
          <span class="p-test-table-color-box p-test-table-color-red-lighter"></span>
          <span>41-60%</span>
        </div>
      </div>
      <div class="p-test-table-wrapper-result" :key="`${selectedClassroom?.uuid}_${selectedCbtUnit?.id}`">
        <table
          id="js-test-table"
          ref="cbtReportResultsTableRef"
          class="p-test-table tablesorter"
        >
          <!-- テーブルヘッダー -->
          <thead>
            <tr>
              <th rowspan="2" class="p-test-table-header-sortable number">出席番号</th>
              <th rowspan="2" class="name">名前</th>
              <th colspan="3" class="first">
                <div class="p-test-table-header-buttons">
                  <a
                    target="_blank"
                    class="c-button-white -small"
                    :href="answerResultsButtonUrl()"
                    data-copy-ignore
                  ><span class="label">解答確認・修正</span></a>
                  <copy-table-button
                    :target-table="cbtReportResultsTableRef"
                    small
                    data-copy-ignore
                  />
                </div>
              </th>
            </tr>
            <tr>
              <th class="p-test-table-header-sortable score second">知識・技能</th>
              <th class="p-test-table-header-sortable score second">思判表</th>
              <th class="p-test-table-header-sortable score second">合計</th>
            </tr>
          </thead>

          <!-- テーブルボディ -->
          <tbody>
            <tr v-for="(student, index) in cbtScoreResults?.students" :key="index" 
              :class="{'gray': submissionStatus(student.id) === SUBMISSION_STATUS.UNSUBMITTED || submissionStatus(student.id) === SUBMISSION_STATUS.UNANSWERED}"
            >
              <td class="row-number u-text-ellipsis">{{ student.attendance_number }}</td>
              <td class="row-name">
                <div class="u-text-ellipsis">{{ student.name }}</div>
                <span class="c-parts-unsubmitted" v-if="submissionStatus(student.id) === SUBMISSION_STATUS.UNSUBMITTED">
                  <span class="c-parts-tooltip">未提出
                    <span class="c-parts-tooltip__description">児童/生徒に提出を指示してください。</span>
                  </span>
                </span>
                <span class="c-parts-unanswered" v-if="submissionStatus(student.id) === SUBMISSION_STATUS.UNANSWERED">
                  <span class="c-parts-tooltip">未解答
                    <span class="c-parts-tooltip__description">この児童/生徒はテストを受けていません。</span>
                  </span>
                </span>
              </td>
              <td v-bind="bindColorattrs(student.scores[0].score, cbtScoreResults?.point_value.scores[0].point)">{{ scoreDisplay(student.scores[0].score, submissionStatus(student.id)) }}</td>
              <td v-bind="bindColorattrs(student.scores[1].score, cbtScoreResults?.point_value.scores[1].point)">{{ scoreDisplay(student.scores[1].score, submissionStatus(student.id)) }}</td>
              <td v-bind="bindColorattrs(student.total_score, cbtScoreResults?.point_value.total_point)">{{ scoreDisplay(student.total_score, submissionStatus(student.id)) }}</td>
            </tr>
          </tbody>

          <!-- テーブルフッター -->
          <tfoot>
            <tr class="p-test-table-footer">
              <th colspan="2">配点</th>
              <td>{{ displayValue(cbtScoreResults?.point_value.scores[0].point) }}</td>
              <td>{{ displayValue(cbtScoreResults?.point_value.scores[1].point) }}</td>
              <td class="total">{{ displayValue(cbtScoreResults?.point_value.total_point) }}</td>
            </tr>
            <tr class="p-test-table-footer">
              <th rowspan="2">平均</th>
              <th>学年全体</th>
              <td>{{ displayValue(cbtScoreResults?.average.grade.scores[0].score) }}</td>
              <td>{{ displayValue(cbtScoreResults?.average.grade.scores[1].score) }}</td>
              <td class="total">{{ displayValue(cbtScoreResults?.average.grade.total_score) }}</td>
            </tr>
            <tr class="p-test-table-footer">
              <th>表示学級</th>
              <td>{{ displayValue(cbtScoreResults?.average.classroom.scores[0].score) }}</td>
              <td>{{ displayValue(cbtScoreResults?.average.classroom.scores[1].score) }}</td>
              <td class="total">{{ displayValue(cbtScoreResults?.average.classroom.total_score) }}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
  import { onMounted, ref, computed, nextTick, watch } from "vue";
  import { useCbtStore } from "@/store/teacher/cbt_store";
  import remove_markdown from "@/util/remove_markdown";
  import {
    ISelectSingleOption,
    ICbtSubjectFieldSelectOption,
    ICbtUnit,
    ICbtClassroomStatuses,
    ICbtScoreResults,
    IClassroomSelectOption,
  } from "@/consts/interfaces";
  // NFR-6554: Import jQuery and tablesorter types (if available)
  declare var $: any;

  const cbtStore = useCbtStore();
  // CBT回ごとの各学級の実施状況
  const cbtClassroomStatuses = ref<ICbtClassroomStatuses | null>(null);
  const academicSession = ref<ISelectSingleOption | null>(null);
  const academicYear = ref<ISelectSingleOption | null>(null);
  const classrooms = ref<IClassroomSelectOption[]>([])
  const selectedClassroom = ref<IClassroomSelectOption | null>(null);
  const subjectField = ref<ICbtSubjectFieldSelectOption | null>(null);
  const cbtUnits = ref<ICbtUnit[]>([]);
  const selectedCbtUnit = ref<ICbtUnit | null>(null);
  const cbtScoreResults = ref<ICbtScoreResults | null>(null);
  const cbtReportResultsTableRef = ref<HTMLTableElement | null>(null);
  // show the blank page or not
  const showBlankPage = ref<boolean>(false);
  
  const isLoading = ref<boolean>(true);

  const SUBMISSION_STATUS = {
    UNANSWERED: 1,
    UNSUBMITTED: 2,
  }

  // 初期表示時
  onMounted(async() => {
    const params = getInitialQueryParams();

    // パラメータが不足している場合は、API呼び出しをスキップ
    if (!Object.values(params).every(v => v !== null)) {
      console.error('Required query parameters are missing:', params);
      return;
    }

    // 画面上部に表示するために、クエリパラメーターから「年度」「学年」「学級」「教科」「項目」を取得
    // NOTE: fetchCbtYearClass() で、
    // エラー、かつ、404 (有効なCBT教材のライセンスがありません) の場合、
    // ブランクページを表示する。以降の処理も不要なのでここで終了。

    const cbtYearClassResponse: any = await cbtStore.fetchCbtYearClass();
    if ("E005" === cbtYearClassResponse?.code) {
      showBlankPage.value = true;
      isLoading.value = false;
      return;
    }
    const cbtSubjectFieldsResponse = await cbtStore.fetchCbtSubjectFields(
      {
        academic_session_id: Number(params.academic_session_id),
        academic_year: Number(params.academic_year),
      }
    );
    const cbtUnitsResponse = await cbtStore.fetchCbtUnits(
      {
        academic_session_id: Number(params.academic_session_id),
        academic_year: Number(params.academic_year),
        subject_field_id: Number(params.subject_field_id),
      }
    );
    const cbtClassroomStatusesResponse = await cbtStore.fetchCbtClassroomStatuses(
      {
        academic_session_id: Number(params.academic_session_id),
        academic_year: Number(params.academic_year),
        subject_field_id: Number(params.subject_field_id),
      }
    );
    const cbtScoreResultsResponse = await cbtStore.fetchCbtScoreResults(Number(params.cbt_unit_id), params.classroom);
    
    academicSession.value = cbtYearClassResponse?.academic_sessions.find(x => x.value === Number(params.academic_session_id)) ?? null;
    academicYear.value = cbtYearClassResponse?.academic_years[Number(params.academic_session_id)]?.find(x => x.value === Number(params.academic_year)) ?? null;
    cbtClassroomStatuses.value = cbtClassroomStatusesResponse ?? null;
    classrooms.value = cbtClassroomStatuses.value?.header_classrooms ?? []
    selectedClassroom.value = classrooms.value?.find(x => x.uuid === params.classroom) ?? null;
    subjectField.value = cbtSubjectFieldsResponse?.find(x => x.id === Number(params.subject_field_id)) ?? null;
    cbtUnits.value = cbtUnitsResponse?.cbt_units ?? []
    selectedCbtUnit.value = cbtUnits.value.find(x => x.id === Number(params.cbt_unit_id)) ?? null;
    cbtScoreResults.value = cbtScoreResultsResponse ?? null;
    if(cbtScoreResults.value === null || cbtScoreResults.value.students.length === 0) {
      showBlankPage.value = true;
    }
    isLoading.value = false;
  })

  const cbtClassroomStudentStatus = computed(() => {
      const classroomStatusForUnit = cbtClassroomStatuses.value?.classroom_statuses_per_cbt_unit.find(unit => unit.cbt_unit_id === selectedCbtUnit.value?.id) ?? null;
      const classroomStatus = classroomStatusForUnit?.classroom_statuses.find(status => status.classroom_uuid === selectedClassroom.value?.uuid) ?? null;
      return {
        number_of_submitted_students: classroomStatus?.number_of_submitted_students ?? "-",
        total_number_of_students: classroomStatus?.total_number_of_students ?? "-",
        unsubmitted_students: classroomStatus?.unsubmitted_students ?? [],
      };
  })

  function submissionStatus(studentId: number): number | null {
    const submission_status = cbtClassroomStudentStatus.value.unsubmitted_students.find(student => student.id === studentId) ?? null;
    return submission_status?.submission_status ?? null;
  }

  // NFR-6554: Functions for initializing and updating the table sorter
  const initTableSorter = () => {
    nextTick(() => {
      const table = $("#js-test-table");
      // Initialize or Update
      table.tablesorter({
        headers: {
          1: { sorter: false }, // Disables "名前"
          2: { sorter: false }  // Disables the "colspan=3" header
        }
      });
      // Crucial: Tell tablesorter to re-scan the rows if data changed
      table.trigger("update");
    });
  };

  // NFR-6554: Watch the data: if students are added/removed, refresh the sorter
  watch(() => cbtScoreResults.value?.students, () => {
    initTableSorter();
  }, { deep: true });


  // クエリパラメーターを正規化するユーティリティ関数
  const normalizeQueryParam = (x: string | null): string | null => x && x.trim() !== "" ? x : null;

  // 集計・分析の検索画面から渡されたクエリパラメーターを取得する
  function getInitialQueryParams() {
    const params = new URLSearchParams(window.location.search);
    return {
      academic_session_id: normalizeQueryParam(params.get("academic_session_id")),
      academic_year: normalizeQueryParam(params.get("academic_year")),
      classroom: normalizeQueryParam(params.get("classroom")),
      subject_field_id: normalizeQueryParam(params.get("subject_field_id")),
      cbt_unit_id: normalizeQueryParam(params.get("cbt_unit_id")),
    }
  }

  function removeMarkDown(text: string) : string | null {
    if(!text) return null
    return remove_markdown(text)
  }

  function changeClassroom(classroom: IClassroomSelectOption) {
    selectedClassroom.value = classroom;
    updateQueryParam("classroom", classroom.uuid)
  }

  function changeCbtUnit(cbtUnit: ICbtUnit) {
    selectedCbtUnit.value = cbtUnit;
    updateQueryParam("cbt_unit_id", `${cbtUnit.id}`)
  }

  async function updateQueryParam(key: string, value: string) {
    // 1. Get the current URL
    const url = new URL(window.location.href);
    
    // 2. Use URLSearchParams to set the new value
    url.searchParams.set(key, value);
    
    // 3. Update the browser's address bar without reloading
    window.history.pushState({}, '', url);
    // 4. fetch the result
    if(selectedCbtUnit.value && selectedClassroom.value){
      const cbtScoreResultsResponse = await cbtStore.fetchCbtScoreResults(selectedCbtUnit.value.id, selectedClassroom.value.uuid);
      cbtScoreResults.value = cbtScoreResultsResponse ?? null;
      if(cbtScoreResults.value === null || cbtScoreResults.value.students.length === 0) {
        showBlankPage.value = true;
      } else {
        showBlankPage.value = false;
      }
    }
  }

  // NFR-7141: get the value or "-" if the value is null or undefined for student "得点"
  function scoreDisplay(score: number | null | undefined, status: number): string | number | null {
    if(status === SUBMISSION_STATUS.UNSUBMITTED || status === SUBMISSION_STATUS.UNANSWERED) {
      return score;
    }
    return score !== null ? score : "-";
  }

  // NFR-7141: get the value or "-" if the value is null or undefined for "配点", "学年全体の平均", "表示学級の平均"
  function displayValue(value: number | null | string): string {
    if (value === null || value === undefined) {
      return "-";
    }
    return String(value);
  }

  // bind color attributes for score cells
  function bindColorattrs(score: number | null | string, totalPoint: number | null | string) {
    if (score === null || score === '-' || totalPoint === null || totalPoint === '-') {
      return {};
    }
    if (totalPoint === 0) {
      return { "class": "p-test-table-color-red-medium" };
    }
    const percentage = (Number(score) / Number(totalPoint)) * 100;
    if (percentage <= 20) {
      return { "class": "p-test-table-color-red-medium" };
    } else if (percentage > 20 && percentage <= 40) {
      return { "class": "p-test-table-color-red-light" };
    } else if (percentage > 40 && percentage <= 60) {
      return { "class": "p-test-table-color-red-lighter" };
    } else {
      return {};
    }
  }

  function answerResultsButtonUrl() {
    if(!academicSession.value || !academicYear.value || !selectedClassroom.value || !subjectField.value || !selectedCbtUnit.value) {
      return "#";
    }
    const params = new URLSearchParams({
      academic_session_id: String(academicSession.value.value),
      academic_year: String(academicYear.value.value),
      classroom: String(selectedClassroom.value.uuid),
      subject_field_id: String(subjectField.value.id),
      cbt_unit_id: String(selectedCbtUnit.value.id),
    });
    return `/teacher/cbt/answer/?${params.toString()}`;
  }

</script>

<style scoped>
</style>